import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../contexts/AuthContext';
import RatingComponent from './RatingComponent';
import './ReservaDetail.css';

const ReservaDetail = ({ reserva, onClose, onEvaluacionActualizada }) => {
    const { user } = useContext(AuthContext);
    const [reservaDetalle, setReservaDetalle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRating, setShowRating] = useState(false);
    const [evaluandoReserva, setEvaluandoReserva] = useState(false);

    useEffect(() => {
        if (reserva && reserva.id) {
            fetchReservaDetalle();
        }
    }, [reserva]);

    const fetchReservaDetalle = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await fetch(`/api/mis-reservas/detalle/${reserva.id}`);
            const data = await response.json();
            
            if (data.success) {
                setReservaDetalle(data.data);
            } else {
                setError(data.message || 'Error al cargar el detalle de la reserva');
            }
        } catch (err) {
            console.error('Error al obtener detalle de reserva:', err);
            setError('Error de conexión al servidor');
        } finally {
            setLoading(false);
        }
    };

    const handleEvaluar = () => {
        setShowRating(true);
    };

    const handleRatingSubmit = async (rating) => {
        try {
            setEvaluandoReserva(true);
            
            const response = await fetch(`/api/mis-reservas/evaluar/${reserva.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    evaluacion: rating,
                    email: user.email
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                // Actualizar el detalle local
                setReservaDetalle(prev => ({
                    ...prev,
                    evaluacion: rating,
                    fecha_evaluacion: new Date().toISOString(),
                    puede_evaluar: false
                }));
                
                setShowRating(false);
                
                // Notificar al componente padre
                if (onEvaluacionActualizada) {
                    onEvaluacionActualizada();
                }
            } else {
                setError(data.message || 'Error al guardar la evaluación');
            }
        } catch (err) {
            console.error('Error al evaluar reserva:', err);
            setError('Error de conexión al servidor');
        } finally {
            setEvaluandoReserva(false);
        }
    };

    const handleRatingCancel = () => {
        setShowRating(false);
    };

    const formatearFecha = (fecha) => {
        return new Date(fecha).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatearPrecio = (precio) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(precio);
    };

    const getEstadoColor = (estado) => {
        switch (estado?.toLowerCase()) {
            case 'confirmada':
                return 'estado-confirmada';
            case 'pendiente':
                return 'estado-pendiente';
            case 'cancelada':
                return 'estado-cancelada';
            case 'completada':
                return 'estado-completada';
            default:
                return 'estado-default';
        }
    };

    const renderEstrellas = (evaluacion) => {
        if (!evaluacion) return null;
        
        return (
            <div className="estrellas-evaluacion">
                {[1, 2, 3, 4, 5].map(star => (
                    <span 
                        key={star} 
                        className={`estrella ${star <= evaluacion ? 'activa' : ''}`}
                    >
                        ★
                    </span>
                ))}
            </div>
        );
    };

    return (
        <div className="reserva-detail-overlay" onClick={onClose}>
            <div className="reserva-detail-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Detalle de Reserva</h2>
                    <button className="btn-close" onClick={onClose}>
                        ✕
                    </button>
                </div>

                {loading && (
                    <div className="modal-loading">
                        <div className="loading-spinner"></div>
                        <p>Cargando detalle...</p>
                    </div>
                )}

                {error && (
                    <div className="modal-error">
                        <p>{error}</p>
                        <button onClick={fetchReservaDetalle} className="btn-retry">
                            Intentar de nuevo
                        </button>
                    </div>
                )}

                {reservaDetalle && !loading && (
                    <div className="modal-content">
                        {/* Header con título y estado */}
                        <div className="detalle-header">
                            <div className="habitacion-info">
                                <h3>{reservaDetalle.habitacion_titulo}</h3>
                                <p className="habitacion-direccion">📍 {reservaDetalle.habitacion_direccion}</p>
                            </div>
                            <span className={`estado-badge ${getEstadoColor(reservaDetalle.estado_reserva)}`}>
                                {reservaDetalle.estado_reserva}
                            </span>
                        </div>

                        {/* Imágenes de la habitación */}
                        {reservaDetalle.imagenes && reservaDetalle.imagenes.length > 0 && (
                            <div className="habitacion-imagenes">
                                <div className="imagenes-grid">
                                    {reservaDetalle.imagenes.slice(0, 3).map((imagen, index) => (
                                        <div key={imagen.id} className={`imagen-container ${index === 0 ? 'imagen-principal' : ''}`}>
                                            <img 
                                                src={imagen.url_imagen} 
                                                alt={imagen.alt_text || reservaDetalle.habitacion_titulo}
                                                onError={(e) => {
                                                    e.target.src = '/placeholder-room.jpg';
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Información detallada */}
                        <div className="detalle-info">
                            <div className="info-section">
                                <h4>📅 Fechas de la Reserva</h4>
                                <div className="fechas-container">
                                    <div className="fecha-item">
                                        <span className="fecha-label">Check-in:</span>
                                        <span className="fecha-valor">{formatearFecha(reservaDetalle.fecha_checkin)}</span>
                                    </div>
                                    <div className="fecha-item">
                                        <span className="fecha-label">Check-out:</span>
                                        <span className="fecha-valor">{formatearFecha(reservaDetalle.fecha_checkout)}</span>
                                    </div>
                                    <div className="fecha-item">
                                        <span className="fecha-label">Duración:</span>
                                        <span className="fecha-valor">{reservaDetalle.duracion_dias} día{reservaDetalle.duracion_dias !== 1 ? 's' : ''}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="info-section">
                                <h4>💰 Información de Pago</h4>
                                <div className="pago-info">
                                    <div className="pago-item">
                                        <span>Total pagado:</span>
                                        <span className="precio-total">{formatearPrecio(reservaDetalle.precio_total)}</span>
                                    </div>
                                    <div className="pago-item">
                                        <span>Método de pago:</span>
                                        <span>{reservaDetalle.metodo_pago}</span>
                                    </div>
                                    <div className="pago-item">
                                        <span>Tipo de reserva:</span>
                                        <span>{reservaDetalle.tipo_reserva}</span>
                                    </div>
                                    {reservaDetalle.wompi_trans_id && (
                                        <div className="pago-item">
                                            <span>ID de transacción:</span>
                                            <span className="transaction-id">{reservaDetalle.wompi_trans_id}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="info-section">
                                <h4>👥 Detalles de la Reserva</h4>
                                <div className="reserva-detalles">
                                    <div className="detalle-item">
                                        <span>Número de huéspedes:</span>
                                        <span>{reservaDetalle.numero_huespedes}</span>
                                    </div>
                                    <div className="detalle-item">
                                        <span>Fecha de reserva:</span>
                                        <span>{formatearFecha(reservaDetalle.created_at)}</span>
                                    </div>
                                    {reservaDetalle.observaciones && (
                                        <div className="detalle-item observaciones">
                                            <span>Observaciones:</span>
                                            <span>{reservaDetalle.observaciones}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Descripción de la habitación */}
                            {reservaDetalle.habitacion_descripcion && (
                                <div className="info-section">
                                    <h4>🏠 Descripción de la Habitación</h4>
                                    <p className="habitacion-descripcion">{reservaDetalle.habitacion_descripcion}</p>
                                </div>
                            )}

                            {/* Evaluación existente o opción para evaluar */}
                            {reservaDetalle.evaluacion ? (
                                <div className="info-section evaluacion-section">
                                    <h4>⭐ Tu Evaluación</h4>
                                    <div className="evaluacion-existente">
                                        {renderEstrellas(reservaDetalle.evaluacion)}
                                        <p className="evaluacion-fecha">
                                            Evaluado el {formatearFecha(reservaDetalle.fecha_evaluacion)}
                                        </p>
                                        {reservaDetalle.puede_evaluar && (
                                            <button 
                                                onClick={handleEvaluar}
                                                className="btn-reevaluar"
                                                disabled={evaluandoReserva}
                                            >
                                                Cambiar Evaluación
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : reservaDetalle.puede_evaluar ? (
                                <div className="info-section evaluacion-section">
                                    <h4>⭐ Evalúa tu Experiencia</h4>
                                    <p>¿Cómo fue tu estadía? Tu opinión nos ayuda a mejorar.</p>
                                    <button 
                                        onClick={handleEvaluar}
                                        className="btn-evaluar"
                                        disabled={evaluandoReserva}
                                    >
                                        {evaluandoReserva ? 'Guardando...' : 'Evaluar Reserva'}
                                    </button>
                                </div>
                            ) : (
                                <div className="info-section">
                                    <div className="no-evaluacion">
                                        <p>Esta reserva no puede ser evaluada en este momento.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Modal de evaluación */}
                {showRating && (
                    <RatingComponent
                        onSubmit={handleRatingSubmit}
                        onCancel={handleRatingCancel}
                        isLoading={evaluandoReserva}
                        currentRating={reservaDetalle?.evaluacion}
                    />
                )}
            </div>
        </div>
    );
};

export default ReservaDetail;